import { Toast as ToastPrimitive } from '@base-ui/react/toast'

import { CircleCheck, CircleX, Loader, X } from '@/lib/icons'
import { cn, variants } from '@/lib/utils'

const viewport = variants('toast-viewport')
const root = variants(
  'toast-root rounded-lg border bg-popover text-popover-foreground shadow-lg outline-hidden focus-visible:ring-2 focus-visible:ring-ring data-[type=error]:border-destructive/40 data-[type=success]:border-success/40'
)
const rootAnimation = variants('motion-reduce:transition-opacity')
const content = variants('gap-3 p-4 pr-9 flex items-start')
// No select-none: error text and IDs must stay copyable.
// Webfluential: font-sans so the app's global h2 heading font doesn't reach the toast title.
const title = variants('text-sm leading-tight font-medium font-sans')
const icon = variants('size-4 mt-px shrink-0')
const typeIcon = variants({
  success: 'text-success',
  error: 'text-destructive',
  loading: 'text-muted-foreground',
})
const loadingAnimation = variants('animate-spin motion-reduce:animate-none')
const description = variants('text-sm text-muted-foreground')
const action = variants(
  'px-2.5 py-1 text-xs font-medium ml-auto shrink-0 self-center rounded-md border outline-hidden hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring'
)
const close = variants(
  'top-2 right-2 [&_svg]:size-4 size-7 absolute flex items-center justify-center rounded-md text-muted-foreground outline-hidden hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
)
const pressAnimation = variants(
  'transition-control duration-fast ease-out active:scale-95 motion-reduce:transition-opacity'
)

/**
 * Module-level manager: call `toast.add({ title })` from anywhere, including Inertia callbacks.
 * `<Toaster />` renders what it holds.
 */
const toast = ToastPrimitive.createToastManager()

/** Hook version, for components inside `<Toaster />`. */
const useToast = ToastPrimitive.useToastManager

type ToasterProps = Omit<ToastPrimitive.Provider.Props, 'children'> & {
  /** Swipe-to-dismiss directions. */
  swipeDirection?: ToastPrimitive.Root.Props['swipeDirection']
}

/**
 * Mount once (e.g. in the app layout). Renders the toast stack bottom-right; hover or focus
 * to expand it. Defaults: 5s timeout, 3 visible.
 */
function Toaster({
  toastManager = toast,
  swipeDirection = ['down', 'right'],
  ...props
}: ToasterProps) {
  return (
    <ToastPrimitive.Provider toastManager={toastManager} {...props}>
      <ToastPrimitive.Portal>
        <ToastPrimitive.Viewport data-slot="toast-viewport" className={viewport}>
          <ToastList swipeDirection={swipeDirection} />
        </ToastPrimitive.Viewport>
      </ToastPrimitive.Portal>
    </ToastPrimitive.Provider>
  )
}

function ToastList({ swipeDirection }: Pick<ToasterProps, 'swipeDirection'>) {
  const { toasts } = useToast()
  return toasts.map((t) => (
    <ToastPrimitive.Root
      key={t.id}
      toast={t}
      data-slot="toast"
      swipeDirection={swipeDirection}
      className={cn(root, rootAnimation)}
    >
      <ToastPrimitive.Content data-slot="toast-content" className={content}>
        <ToastIcon type={t.type} />
        <div className="min-w-0 gap-1 flex flex-col">
          <ToastPrimitive.Title data-slot="toast-title" className={title} />
          {/* Webfluential: rich descriptions (alerts with a button, avatar rows) are block markup,
              which can't sit in the default <p>. */}
          <ToastPrimitive.Description
            data-slot="toast-description"
            className={description}
            render={isText(t.description) ? undefined : <div />}
          />
        </div>
        {t.actionProps && (
          <ToastPrimitive.Action data-slot="toast-action" className={cn(action, pressAnimation)} />
        )}
      </ToastPrimitive.Content>
      <ToastPrimitive.Close
        data-slot="toast-close"
        className={cn(close, pressAnimation)}
        aria-label="Close"
      >
        <X aria-hidden />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  ))
}

const isText = (value: unknown) =>
  value == null || typeof value === 'string' || typeof value === 'number'

/** Success and error never rely on the border tint alone. */
function ToastIcon({ type }: { type?: string }) {
  if (type === 'success') return <CircleCheck aria-hidden className={cn(icon, typeIcon.success)} />
  if (type === 'error') return <CircleX aria-hidden className={cn(icon, typeIcon.error)} />
  if (type === 'loading')
    return <Loader aria-hidden className={cn(icon, typeIcon.loading, loadingAnimation)} />
  return null
}

export { Toaster, toast, useToast, type ToasterProps }
